import ReactTextareaAutocomplete, {
  ItemComponentProps,
} from "@webscopeio/react-textarea-autocomplete";
import "@webscopeio/react-textarea-autocomplete/style.css";
import { Button, Tag, message } from "antd";
import { FC, TextareaHTMLAttributes, useEffect, useState } from "react";
import { EquationParser } from "../../utils/equationParser";
import { validateEquationContent } from "../../utils/equationValidation";
import OperatorGuideModal from "./OperatorGuideModal";
import { IPQuestion } from "modules/organization/models/interface";
import {
  RankingToolState,
  useRankingToolStore,
} from "modules/organization/store";
import { useShallow } from "zustand/react/shallow";

interface IQuestionVariable {
  id: number;
  variable: string;
  question: string;
  question_order_text: string;
}

interface IQuestionResponseVariable {
  id: number;
  variable: string;
  question: string;
}

interface IOperator {
  name: string;
}

const RankingToolsEditor: FC<{
  questions?: IPQuestion[];
  projectSlug?: string;
  rankingProjectId?: number;
  rankingTypeId?: number | null;
}> = ({ questions, projectSlug, rankingProjectId, rankingTypeId }) => {
  const isPointBased = rankingTypeId === 2;
  const ranks = useRankingToolStore(
    useShallow((state: RankingToolState) => state.ranks),
  );
  const currentRankTab = useRankingToolStore(
    (state: RankingToolState) => state.currentRankTab,
  );
  const currentEquation = useRankingToolStore(
    useShallow((state: RankingToolState) => state.currentEquation),
  );
  const allEquations = useRankingToolStore(
    useShallow((state: RankingToolState) => state.allEquations),
  );
  const setCurrentTab = useRankingToolStore(
    (state: RankingToolState) => state.setCurrentTab,
  );
  const setEquationText = useRankingToolStore(
    (state: RankingToolState) => state.setEquationText,
  );
  const setScrollToVariable = useRankingToolStore(
    (state: RankingToolState) => state.setScrollToVariable,
  );
  const isSavingQuestionSnapshot = useRankingToolStore(
    (state: RankingToolState) => state.isSavingQuestionSnapshot,
  );
  const isQuestionSnapshotDirty = useRankingToolStore(
    (state: RankingToolState) => state.isQuestionSnapshotDirty,
  );
  const savedEquations = useRankingToolStore(
    useShallow((state: RankingToolState) => state.savedEquations),
  );
  const isSavingEquation = useRankingToolStore(
    (state: RankingToolState) => state.isSavingEquation,
  );
  const saveQuestionSnapshot = useRankingToolStore(
    (state: RankingToolState) => state.saveQuestionSnapshot,
  );
  const saveRankingEquation = useRankingToolStore(
    (state: RankingToolState) => state.saveRankingEquation,
  );
  const clearEquation = useRankingToolStore(
    (state: RankingToolState) => state.clearEquation,
  );
  const clearResponseScores = useRankingToolStore(
    (state: RankingToolState) => state.clearResponseScores,
  );
  const isCurrentEquationDirty =
    (currentEquation?.equation ?? "") !==
    (savedEquations[currentRankTab] ?? "");
  const [questionVariableList, setQuestionVariableList] = useState<
    IQuestionVariable[]
  >([]);
  const [questionResponseVariableList, setQuestionResponseVariableList] =
    useState<IQuestionResponseVariable[]>([]);
  const [operatorList, setOperatorList] = useState<IOperator[]>([
    { name: "AND" },
    { name: "QAND" },
    { name: "OR" },
    { name: "QOR" },
    { name: "EQUAL" },
    { name: "NOTEQUAL" },
    { name: "COMPARE" },
    { name: "QHASANSWER" },
    { name: "QHASNOTANSWER" },
    { name: "IMPORT" },
  ]);

  let textAreaRef: ReactTextareaAutocomplete<
    string | object,
    TextareaHTMLAttributes<HTMLTextAreaElement>
  > | null = null;

  useEffect(() => {
    if (questions) {
      const _buildQuestionVariableList: IQuestionVariable[] = [];
      const _buildQuestionResponseVariableList: IQuestionResponseVariable[] =
        [];

      questions.forEach((eachQuestion) => {
        _buildQuestionVariableList.push({
          id: eachQuestion.id,
          variable: eachQuestion.question_variable,
          question: eachQuestion.question,
          question_order_text: eachQuestion.question_order_text,
        });

        eachQuestion.responses.forEach((eachQResponse) => {
          _buildQuestionResponseVariableList.push({
            id: eachQResponse.id,
            variable: eachQResponse.response_variable,
            question: eachQuestion.question,
          });
        });
      });

      setQuestionVariableList(_buildQuestionVariableList);
      setQuestionResponseVariableList(_buildQuestionResponseVariableList);
    }
  }, [questions]);

  const operatorDataProvider = (token: string) => {
    return operatorList
      .filter((each) => each.name.toLowerCase().includes(token.toLowerCase()))
      .map((each) => {
        return {
          ...each,
          caretPosition: "start",
        };
      });
  };

  const questionDataProvider = (token: string) => {
    return questionVariableList.filter(
      (each) =>
        each.variable.toLowerCase().includes(token.toLowerCase()) ||
        each.question.toLowerCase().includes(token.toLowerCase()),
    );
  };

  const questionResponseDataProvider = (token: string) => {
    return questionResponseVariableList.filter(
      (each) =>
        each.variable.toLowerCase().includes(token.toLowerCase()) ||
        each.question.toLowerCase().includes(token.toLowerCase()),
    );
  };

  // Lists this Ranking Project's other ranks — for IMPORT(<rank>), same shortcut pattern as
  // @/#/: for questions/responses/operators. Excludes the currently selected tab (importing
  // yourself isn't meaningful) AND any rank with a smaller display_order than the current one —
  // i.e. a higher rank — since that IMPORT would fail the tier-order check anyway (a lower rank
  // can only import an equal-or-lower rank, never a higher one).
  const rankDataProvider = (token: string) => {
    const selfDisplayOrder = currentEquation?.display_order;
    return ranks
      .filter((rank) => {
        if (rank === currentRankTab) {
          return false;
        }
        if (selfDisplayOrder != null) {
          const targetDisplayOrder = allEquations[rank]?.display_order;
          if (targetDisplayOrder != null && targetDisplayOrder < selfDisplayOrder) {
            return false;
          }
        }
        return rank.toLowerCase().includes(token.toLowerCase());
      })
      .map((rank) => ({ name: rank }));
  };

  const [equationError, setEquationError] = useState(0);
  // Every specific, pointed-out reason the current equation is invalid — the exact syntax
  // condition that failed (e.g. "Operator 'AND' requires at least one parameter.") when there's
  // a syntax error, otherwise every content problem found (unknown variable, unknown IMPORT
  // target, IMPORT tier-order violation) — never a generic "Equation has syntax error" label.
  const [equationErrors, setEquationErrors] = useState<string[]>([]);
  const [isOperatorGuideOpen, setIsOperatorGuideOpen] = useState(false);

  const handleChangeEquation = (data: string) => {
    setEquationText(data);
  };

  useEffect(() => {
    const equationText = currentEquation?.equation ?? "";
    setEquationError(new EquationParser(equationText).getHasError());

    const rankDisplayOrderByName = new Map(
      Object.values(allEquations).map((eq) => [eq.name, eq.display_order]),
    );
    setEquationErrors(
      validateEquationContent(
        equationText,
        questions ?? [],
        rankDisplayOrderByName,
        currentEquation?.display_order,
      ),
    );
  }, [
    currentEquation?.equation,
    currentEquation?.display_order,
    questions,
    allEquations,
  ]);

  const handleSave = async () => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    try {
      await saveQuestionSnapshot(projectSlug, rankingProjectId);
      message.success({
        key: "saveQuestionSnapshot",
        content: "Question snapshot saved successfully.",
        duration: 2,
      });
    } catch (error) {
      console.error(error);
      message.error({
        key: "saveQuestionSnapshot",
        content: "Failed to save question snapshot.",
      });
    }
  };

  const handleClear = () => {
    if (isPointBased) {
      clearResponseScores();
    }
  };

  const handleSaveEquation = async () => {
    if (!projectSlug || !rankingProjectId) {
      return;
    }
    if (equationError > 0 || equationErrors.length > 0) {
      return;
    }
    try {
      await saveRankingEquation(projectSlug, rankingProjectId);
      message.success({
        key: "saveEquation",
        content: `${currentRankTab} equation saved successfully.`,
        duration: 2,
      });
    } catch (error: any) {
      console.error(error);
      // error?.error?.response?.data?.message: a real API failure (see ApiService's
      // onResponseError). error.message on a plain Error: the store's own defensive
      // validateEquationContent re-check (should be unreachable since Save is already disabled
      // whenever equationErrors is non-empty, but keeps the real reason visible if it ever fires).
      const backendMessage = error?.error?.response?.data?.message;
      const contentMessage =
        !backendMessage && error instanceof Error ? error.message : null;
      message.error({
        key: "saveEquation",
        content:
          backendMessage ||
          contentMessage ||
          `Failed to save ${currentRankTab} equation.`,
      });
    }
  };

  const handleClearEquation = () => {
    clearEquation();
  };

  return (
    <div className="tabContainer">
      {!isPointBased && (
        <div className="tabMenu" role="tablist" aria-labelledby="tab-header">
          {ranks.map((rank) => {
            const isRankEquationDirty =
              (allEquations[rank]?.equation ?? "") !==
              (savedEquations[rank] ?? "");
            return (
              <Button
                onClick={() => setCurrentTab(rank)}
                key={rank}
                className={rank === currentRankTab ? "active" : ""}
              >
                <span className="ellipsisTxt rankTabLabel" title={rank}>
                  {rank}
                </span>
                {isRankEquationDirty && (
                  <Tag
                    style={{
                      marginLeft: 6,
                      background: "transparent",
                      border: "1px solid #972B38",
                      color: "#972B38",
                    }}
                  >
                    Unsaved
                  </Tag>
                )}
              </Button>
            );
          })}
        </div>
      )}
      <div
        className={`tabContent${isPointBased ? " tabContentPointBased" : ""}`}
      >
        {!isPointBased && (
          <>
            <h3>Build Equation</h3>
            <ReactTextareaAutocomplete
              value={currentEquation?.equation ?? ""}
              className="rankingTextarea"
              style={{ borderColor: equationError > 0 ? "red" : "#969696" }}
              onChange={(e) => {
                handleChangeEquation(e.target.value);
              }}
              loadingComponent={() => <span>Loading</span>}
              placeholder="Start build equation"
              data-grammar="false"
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
              trigger={{
                "#": {
                  dataProvider: questionResponseDataProvider,
                  component: (item: any) => {
                    return (
                      <div className="entryName">{item.entity.variable}</div>
                    );
                  },
                  output: (item: any, trigger) => item.variable,
                },
                "@": {
                  dataProvider: questionDataProvider,
                  component: (item: any) => {
                    return (
                      <>
                        <div className="entryName">{item.entity.variable}</div>
                        <div>
                          {item.entity.question_order_text + ")"}{" "}
                          {item.entity.question}
                        </div>
                      </>
                    );
                  },
                  output: (item: any, trigger) => item.variable,
                },
                ":": {
                  dataProvider: operatorDataProvider,
                  component: (item: ItemComponentProps<any>) => (
                    <>{`${item.entity?.name}`}</>
                  ),
                  output: (item: any, trigger) => {
                    return {
                      key: item.name,
                      text: item.name + "()",
                      caretPosition: "end",
                    };
                  },
                },
                "$": {
                  dataProvider: rankDataProvider,
                  component: (item: any) => (
                    <div className="entryName">{item.entity.name}</div>
                  ),
                  output: (item: any, trigger) => item.name,
                },
              }}
              onItemSelected={(data) => {
                if (data.currentTrigger === ":") {
                  setTimeout(() => {
                    textAreaRef?.setCaretPosition(
                      textAreaRef?.getCaretPosition() - 1,
                    );
                  }, 100);
                } else if (
                  data.currentTrigger === "#" ||
                  data.currentTrigger === "@"
                ) {
                  const selectedVariable = (data.item as { variable?: string })
                    ?.variable;
                  if (selectedVariable) {
                    setScrollToVariable(selectedVariable);
                  }
                }
              }}
              movePopupAsYouType={true}
              minChar={0}
              // onCaretPositionChange={(num) => {
              //   console.log(num);
              // }}
              ref={(rta) => {
                if (rta?.getCaretPosition()) {
                  textAreaRef = rta;
                }
              }}
            />
            {equationErrors.length > 0 && (
              <ul style={{ color: "red", margin: "4px 0 0"}}>
                {equationErrors.map((eachError, index) => (
                  <li key={index}>{eachError}</li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="btnWrap">
          {!isPointBased && (
            <Button
              className="linkBtn2"
              onClick={() => setIsOperatorGuideOpen(true)}
            >
              Operation Guide
            </Button>
          )}
          {isPointBased ? (
            <>
              <Button onClick={handleClear}>Clear</Button>
              <Button
                type="primary"
                loading={isSavingQuestionSnapshot}
                disabled={
                  !projectSlug || !rankingProjectId || !isQuestionSnapshotDirty
                }
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleClearEquation}
                disabled={!isCurrentEquationDirty}
              >
                Clear
              </Button>
              <Button
                type="primary"
                loading={isSavingEquation}
                disabled={
                  !projectSlug ||
                  !rankingProjectId ||
                  !isCurrentEquationDirty ||
                  equationError > 0 ||
                  equationErrors.length > 0
                }
                onClick={handleSaveEquation}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
      <OperatorGuideModal
        open={isOperatorGuideOpen}
        onClose={() => setIsOperatorGuideOpen(false)}
      />
    </div>
  );
};

export default RankingToolsEditor;
